/* eslint-disable jsx-a11y/control-has-associated-label */
/* eslint-disable react-hooks/exhaustive-deps */
import React, {
    useContext,
    useEffect,
    useRef,
    useState,
    useMemo,
} from 'react';
import { Transition } from 'react-transition-group';
import { useQuery, useReactiveVar } from '@apollo/client';
import PropTypes from 'prop-types';
import ScrollLock from 'react-scrolllock';
import Raven from 'raven-js';
import sortBy from 'lodash/sortBy';
import classNames from 'classnames';
import queryString from 'query-string';
import { useRouterState } from 'app/hooks/useRouterState';
import { analyticService } from 'global/services';
import { useAddToBasketMutation } from 'app/views/Menu/Menu/hooks';
import { URLUpdater } from 'global/utils';

import {
    GET_SUBSCRIPTION_TYPE,
} from 'app/graphql/client/subscription';
import { useSelectedFilters } from 'app/connect/connectToSelectedFilters';

import { isSubscription } from 'app/const/subscription';
import { MOBILE_ANIMATION_PARAMS } from 'app/const/menuAnimation';
import { CATEGORIES_ORDER } from 'app/const/categories';

import { LocaleContext } from 'app/containers/LocaleProvider';
import { MenuDispatchContext } from 'app/containers/contexts/menu.context';
import { DishesSelectionContext } from 'app/containers/contexts/dishesSelection.context';

import MobileCartHeader from 'app/components/MobileCartHeader';
import OpenBasketButton from 'app/components/OpenBasketButton';
import Dishes from 'app/components/Dishes';
import { UIBanner, UIParagraph } from 'app/components/ui';
import { selectedPeriodVar } from 'app/apollo/reaction';

import {
    selectDishes,
} from 'app/utils/dish';


import './dishes-selection.scss';
import MenuDishesProvider from 'app/containers/MenuDishesProvider';
import { FiltersContext } from 'app/containers/contexts/filters.context/filters.context';
import { useBasketQueryLocate } from 'app/hooks/useBasketQueryLocate';
import { useMenuPeriodsData } from 'app/hooks/accessors';
import { useCartMutationInBasket } from 'app/views/Basket/hooks';
import { abTestDataContext } from 'app/containers/contexts/abTest.context';
import { getBannerContent } from './dishesSelection.record';

const SelectionPopOverBanner = ({ tag = null, realm = '' }) => {
    const bannerContent = getBannerContent(tag);
    const backgroundImagePath = bannerContent?.backgroundImagePath;
    const description = bannerContent?.description;
    const bgSize = bannerContent?.bgSize;
    const title = bannerContent?.title;

    const bannerDescriptionStyles = classNames({
        'selection-poppver-banner-description': true,
        [bgSize]: true,
    });

    const isNewUpsellSelection = realm === 'new_upsell';

    return (
        <div styleName={`selection-popover-banner-wrapper ${isNewUpsellSelection && 'category-selection'}`}>
            {!backgroundImagePath && (
                <p styleName="selection-poppver-banner-title black centered">
                    {title}
                </p>
            )}
            {backgroundImagePath && (
                <UIBanner bannerSize="m" backgroundImage={backgroundImagePath} bgSize={bgSize}>
                    <p styleName="selection-poppver-banner-title">
                        {title}
                    </p>
                </UIBanner>
            )}
            {description && (
                <UIParagraph level="3.1" styleName={bannerDescriptionStyles}>
                    {description}
                </UIParagraph>
            )}
        </div>
    );
};
SelectionPopOverBanner.propTypes = {
    tag: PropTypes.string.isRequired,
    realm: PropTypes.string,
};

const DishesSelectionButton = ({
    // ACTION
    onClickOpenBasket, onCloseSelection,
    // DATA
    price, loading, realm = '',
}) => {
    const realmsToCloseBasket = ['basket_realm', 'new_upsell'];

    const onClick = realmsToCloseBasket.includes(realm)
        ? onCloseSelection
        : onClickOpenBasket;
    return (
        <OpenBasketButton
            price={price}
            onClick={onClick}
            loading={loading}
        />
    );
};
DishesSelectionButton.propTypes = {
    onClickOpenBasket: PropTypes.func.isRequired,
    onCloseSelection: PropTypes.func.isRequired,
    price: PropTypes.number.isRequired,
    loading: PropTypes.bool.isRequired,
    realm: PropTypes.string.isRequired,
};

function DishesSelection({
    // data,
    overlayIsOpen,
    menuDishesQuery: {
        allDishes,
        basketDishes,
    },
    location = window?.location,
    onClickOpenBasket,
}) {
    const contentRef = useRef();
    const [basketData, refetchBasket] = useBasketQueryLocate();

    const isDeliveryMenu = useMemo(
        () => window.location.pathname.includes('app/delivery'),
        [window.location.pathname],
    );

    const deliveryId = useMemo(
        () => window.location.pathname.split('/').find((e) => Boolean(e) && !Number.isNaN(Number(e))),
        [window.location.pathname],
    );

    const { locale } = useContext(LocaleContext);
    const menuDispatchContext = useContext(MenuDispatchContext);
    const {
        data,
        closeSelectionHeaderHandler,
        onCloseSelectionCTA,
    } = useContext(DishesSelectionContext);

    const { data: { subscriptionType } } = useQuery(GET_SUBSCRIPTION_TYPE);
    const { selectedPeriod } = useSelectedFilters();

    const { tag, realm } = data;

    const [isBasketLoading, setIsBasketLoading] = useState(false);
    const [savedTag, setSavedTag] = useState(tag);
    const { handleAddToBasketMutation } = useAddToBasketMutation();
    const { mutateItemInCart } = useCartMutationInBasket({ delivery_Id: deliveryId });

    useEffect(() => {
        if (tag && tag !== savedTag) {
            setSavedTag(tag);
        }
    }, [tag]);

    useEffect(() => {
        if (data.isOpen) {
            analyticService.push({
                eventName: ' Open_Pop_Over',
                eventLabel: getBannerContent(tag).type,
            });
        }
    }, [data.isOpen]);

    const bannerContent = getBannerContent(tag);
    const configDishes = bannerContent && bannerContent.dishes ? bannerContent.dishes : [];


    const onClickAddToBasket = async (dishData) => {
        const { dishId, portion } = dishData;

        const searchParams = queryString.parse(window.location.search);
        const dishToReplace = searchParams?.dish_to_replace;

        setIsBasketLoading(true);

        sessionStorage.removeItem('toCheckout');
        try {
            if (dishToReplace) {
                mutateItemInCart(
                    {
                        dishes: [
                            { dish_id: dishId, portions: portion },
                            { dish_id: dishToReplace, portions: 0 },
                        ],
                        onCompletedCallback: () => {
                            URLUpdater({ dish_to_replace: null });
                            analyticService
                                .push({
                                    eventName: 'add_to_cart',
                                    dishes: [dishData],
                                    typeOfSet: 'regular',
                                    source: 'change_dish',
                                    subscriptionType,
                                })
                                .push({
                                    eventName: 'Custom_Card_Dish_Delete',
                                    dish_id: dishToReplace,
                                    source: 'basket',
                                });
                        },
                    },
                );
            } else {
                handleAddToBasketMutation({
                    dishId,
                    portion,
                    period: selectedPeriod,
                    deliveryId: isDeliveryMenu ? deliveryId : null,
                });
            }
            await refetchBasket();
        } catch (e) {
            Raven.captureException(e);
        }

        setIsBasketLoading(false);
    };

    const isSubscriptionActivated = isSubscription(subscriptionType);

    const hasConfigDishes = configDishes && configDishes.length > 0;

    const hasCategory = Boolean(bannerContent?.categoryId);
    const isFilterByTagNeeded = !(configDishes && configDishes.length > 0) && !hasCategory;
    const isFilterByCategoryNeeded = hasCategory;

    const filterTags = isFilterByTagNeeded ? [savedTag] : [];
    const filterCategory = isFilterByCategoryNeeded ? bannerContent?.categoryId : null;

    const dishes = selectDishes(allDishes, filterCategory, filterTags, null, basketDishes)
        .filter((dish) => {
            if (!hasConfigDishes) return true;
            const isDishFromConfig = configDishes.includes(dish.id);
            const isDishWithTag = bannerContent.additionalTag
                ? dish.tags.find((t) => t.id === bannerContent.additionalTag)
                : false;

            return isDishFromConfig || isDishWithTag;
        });

    const sortDishesByConfigOrder = () => {
        const dishesFromConfig = configDishes.map((id) => {
            const configDish = dishes.find((d) => d.id === id);
            return configDish;
        }).filter((dish) => Boolean(dish));

        const restDishes = dishes.filter((dish) => {
            const isRest = !configDishes.includes(dish.id);
            return isRest;
        });

        return [
            ...dishesFromConfig,
            ...restDishes,
        ];
    };

    const sortedDishes = hasConfigDishes
        ? sortDishesByConfigOrder()
        : sortBy(
            dishes,
            CATEGORIES_ORDER
                .map((categoryId) => (dish) => String(dish.categoryId) === categoryId)
                .reverse(),
        );

    const menuDishesQuery = {
        menuDishes: sortedDishes,
        loading: false,
        error: null,
    };

    const price = basketData?.cart?.totals?.total_discount_price;

    return (
        <Transition
            in={data.isOpen}
            timeout={500}
            appear
        >
            {(transitionState) => (
                <>
                    {!overlayIsOpen && data.isOpen && contentRef.current && (
                        <ScrollLock touchScrollTarget={contentRef.current} />
                    )}
                    <div styleName={`dishes-selection__wrap ${transitionState} ${realm}`}>
                        {transitionState !== 'exited' && (
                            <>
                                <div styleName="dishes-selection__header">
                                    <MobileCartHeader
                                        isRightBlockShown={false}
                                        onClickBack={closeSelectionHeaderHandler}
                                        onClickClose={closeSelectionHeaderHandler}
                                        realm="selection"
                                    />
                                </div>

                                <div styleName="dishes-selection__content" ref={contentRef}>
                                    {savedTag && <SelectionPopOverBanner tag={savedTag} realm={realm} />}
                                    <div styleName="dishes-selection__list">
                                        <Dishes
                                            categoryTitle=""
                                            delayTime={100}
                                            isDishesUpdating={false}
                                            locale={locale}
                                            isBasketLoading={isBasketLoading}
                                            menuDispatchContext={menuDispatchContext}
                                            menuDishesQuery={menuDishesQuery}
                                            categoryId="selection"
                                            isMobileMenuDishesVisible
                                            isSubscriptionActivated={isSubscriptionActivated}
                                            addToBasketAnimationTarget={MOBILE_ANIMATION_PARAMS}
                                            onClickAddToBasket={onClickAddToBasket}
                                            isDishesSliderNeeded={false}
                                            isAutoscrollingDisabled
                                            subscriptionType={subscriptionType}
                                            location={location}
                                            dishComponentType="mobile"
                                        />
                                    </div>
                                </div>

                                <div styleName="dishes-selection__footer">
                                    <DishesSelectionButton
                                        price={price}
                                        onClickOpenBasket={onCloseSelectionCTA}
                                        loading={isBasketLoading}
                                        location={location}
                                        realm={realm || 'new_upsell'}
                                        onCloseSelection={onCloseSelectionCTA}
                                    />
                                </div>
                            </>
                        )}
                    </div>
                    <button
                        styleName={`dishes-selection-close-layer  ${transitionState}`}
                        type="button"
                        onClick={onCloseSelectionCTA}
                    />
                </>
            )}
        </Transition>
    );
}

DishesSelection.propTypes = {
    overlayIsOpen: PropTypes.bool.isRequired,
    // data
    menuDishesQuery: PropTypes.shape({
        allDishes: PropTypes.arrayOf(PropTypes.shape({})),
        basketDishes: PropTypes.arrayOf(PropTypes.shape({})),
    }).isRequired,
    location: PropTypes.shape({}),
    // callbacks
    onClickOpenBasket: PropTypes.func.isRequired,
};

const DishesSelectionWrapper = ({
    overlayIsOpen,
    onClickOpenBasket,
}) => {
    const selectedPeriod = useReactiveVar(selectedPeriodVar);
    const [{ location }] = useRouterState();

    const menuFilter = useMenuPeriodsData();

    return (
        <MenuDishesProvider
            selectedPeriod={selectedPeriod}
            menuFilter={menuFilter}
            wrapper="DishesSelectionWrapper"
            render={
                (props) => {
                    if (!props.hasDataForRenderDishes) return null;
                    return (
                        <DishesSelection
                            {...props}
                            location={location}
                            overlayIsOpen={overlayIsOpen}
                            onClickOpenBasket={onClickOpenBasket}
                        />
                    );
                }
            }
        />
    );
};

DishesSelectionWrapper.propTypes = {
    onClickOpenBasket: PropTypes.func.isRequired,
    overlayIsOpen: PropTypes.bool.isRequired,
    location: PropTypes.shape({}).isRequired,
};

export default DishesSelectionWrapper;
